import ReloadOutlined from "@ant-design/icons/ReloadOutlined"
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react"

import { isEmbed } from "@/util/embed"
import { Feature, getFeatureNumber } from "@/util/gating"

import "./StaleVersionBanner.less"

interface VersionInfo {
  build_sha: string | null
  build_committed_at: string | null
}

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

const FRONTEND_BUILD_SHA: string = process.env.GIT_SHA || ""
const FRONTEND_COMMITTED_AT: string = process.env.GIT_COMMITTED_AT || ""

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch("/api/version", { credentials: "include" })
    if (!response.ok) {
      return null
    }
    return (await response.json()) as VersionInfo
  } catch {
    return null
  }
}

// The banner is triggered by a build SHA change (a new build is live). The
// threshold only *suppresses* low-value nags: it hides the banner when the
// newly deployed build is at least as new as the running build, but still less
// than `thresholdMinutes` newer. Rollbacks/reverts are meaningful version
// changes, so negative deltas still show the banner. When either commit
// timestamp is missing or unparseable we cannot compute the age gap, so we do
// NOT suppress — the SHA change alone drives the banner.
function isBelowThreshold(serverCommittedAt: string, thresholdMinutes: number): boolean {
  if (!FRONTEND_COMMITTED_AT) {
    return false
  }
  const serverTime = new Date(serverCommittedAt).getTime()
  const frontendTime = new Date(FRONTEND_COMMITTED_AT).getTime()
  if (Number.isNaN(serverTime) || Number.isNaN(frontendTime)) {
    return false
  }
  const deltaMinutes = (serverTime - frontendTime) / 60_000
  return deltaMinutes >= 0 && deltaMinutes < thresholdMinutes
}

export function StaleVersionBanner(): ReactElement | null {
  const [stale, setStale] = useState(false)
  const thresholdRef = useRef<number | undefined>(
    getFeatureNumber(Feature.StaleVersionBannerThresholdMinutes),
  )

  const checkVersion = useCallback(async () => {
    const threshold = thresholdRef.current
    if (threshold === undefined) {
      return
    }

    const info = await fetchVersion()
    if (!info?.build_sha) {
      return
    }

    // Trigger: a new build is live (the SHA only changes on deploy).
    if (info.build_sha === FRONTEND_BUILD_SHA) {
      return
    }

    // Suppress low-value nags: hide the banner only when we can confirm the
    // new build is less than `threshold` minutes newer than the running one.
    if (info.build_committed_at && isBelowThreshold(info.build_committed_at, threshold)) {
      return
    }

    setStale(true)
  }, [])

  const embed = isEmbed()
  const disabled = thresholdRef.current === undefined

  useEffect(() => {
    if (embed || disabled) {
      return undefined
    }

    void checkVersion()

    const interval = setInterval(() => void checkVersion(), POLL_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkVersion()
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [checkVersion, embed, disabled])

  if (!stale || embed || disabled) {
    return null
  }

  return (
    <div className="StaleVersionBanner">
      <span className="StaleVersionBanner__label">A new version is available.</span>
      <button
        type="button"
        className="StaleVersionBanner__button"
        onClick={() => window.location.reload()}
      >
        <ReloadOutlined />
        <span>Refresh to update</span>
      </button>
    </div>
  )
}
